import { Button, Divider, Grid, Paper, Stack } from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Schema } from 'yup';
import MainContentHeader from 'src/shared/components/atoms/mainContentHeader';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import { useForm, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useGlobals } from 'src/shared/contexts/appContext';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import ControlledTextEditor from 'src/shared/components/form/controlledTextEditor';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import { useCallback, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import SendIcon from '@mui/icons-material/Send';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { toast } from 'react-toastify';
import { createSms } from 'src/shared/services/groupMessageSmsService';
import { RegexHelper } from 'src/shared/helpers/regexHelper';
import { SelectionTypeEnum } from '../models/selectionType.enum';
import { GroupMessageSmsRequest } from '../models/groupMessageSmsRequest';
import { SelectionForm } from '../components/selectionForm';
import { FixedSelection } from '../models/fixedSelection';
import { GroupMessageSmsPreview } from '../components/groupMessageSmsPreview';
import { GroupMessageSmsMessageLength } from '../components/groupMessageSmsMessageLength';
import { Selection } from '../models/selection';

export const GroupMessageSmsCreatePage = () => {
  const { currentUser } = useGlobals();
  const { t } = useTranslation(['groupMessage', 'common']);
  const navigate = useNavigate();

  const isMounted = useIsMounted();

  const splitMessageLength = 160; // Number of characters for each SMS message, split strings into multiple messages after n

  const [selection, setSelection] = useState<Selection | null>(null);
  const [fixedSelection, setFixedSelection] = useState<FixedSelection | null>(null);

  const [sendMode, setSendMode] = useState<boolean>(false);
  const [bodyText, setBodyText] = useState<string>('');

  const requiredMessage = t('common:validationMessages.required');

  const GroupMessageSmsRequestSchema: Schema<GroupMessageSmsRequest> = yup.object().shape({
    // copyToEmails: yup.string().email().optional(),
    name: yup.string().required(requiredMessage),
    product_id: yup.number().optional(),
    selection_id: yup.number().required(requiredMessage),
    selectionName: yup.string().optional(),
    recipients: yup
      .string()
      .trim()
      .matches(RegexHelper.mobilePhoneNumbers, {
        message: t('common:validationMessages.multiplePhoneNumbers'),
        excludeEmptyString: true,
      }),
    body: yup.string().required(requiredMessage),
    plainText: yup.string().optional(),
    useLinkCounter: yup.boolean().optional(),
    sendDate: yup.string().optional(),
    sender: yup.string().optional(),
    documentIds: yup.string().optional(),
    localAss_id: yup.number().optional(),
    countyAss_id: yup.number().optional(),
    senderTitle: yup.string().optional(),
    selectionType: yup.number().optional(),
    speedFormValues: yup.string().optional(),
    speedFormText: yup.string().optional(),
  });

  const formMethods = useForm<GroupMessageSmsRequest>({
    resolver: yupResolver(GroupMessageSmsRequestSchema),
    mode: 'all',
    defaultValues: {
      name: '',
      sender: currentUser?.email || '',
      speedFormValues: '',
      speedFormText: '',
      senderTitle: '',
      selectionType: SelectionTypeEnum.Selection,
      body: '',
      recipients: '',
      selection_id: 0,
    },
  });

  const {
    handleSubmit,
    formState: { errors, isSubmitting },
    trigger,
  } = formMethods;

  const postData = useCallback(
    async (data) => {
      ConsoleHelper.log('postData', data);
      try {
        // eslint-disable-next-line no-param-reassign
        data.send = sendMode;
        // eslint-disable-next-line no-param-reassign
        data.body = bodyText;
        // eslint-disable-next-line no-param-reassign
        if (selection) data.selectionName = selection?.selectionName;
        const res = await createSms(data);
        if (isMounted()) {
          ConsoleHelper.log('res createSms', res);
          if (sendMode) toast.success(<>{t('groupMessage:sms.create.sendSuccess')}</>);
          else toast.success(<>{t('groupMessage:sms.create.saveSuccess')}</>);
          navigate(RoutesEnum.GroupMessageSmsOverview);
        }
      } catch (e) {
        if (isMounted()) {
          if (data.send) toast.warning(<>{t('groupMessage:sms.create.sendError')}</>);
          else toast.warning(<>{t('groupMessage:sms.create.saveError')}</>);
          ConsoleHelper.error('saveFormDataError:', e);
        }
      }
    },
    [bodyText, isMounted, navigate, selection, sendMode, t],
  );

  const onSubmit = async (data: GroupMessageSmsRequest) => {
    ConsoleHelper.log('onSubmit', data);
    await trigger();
    await postData(data);
  };

  const textEditorInitalized = useCallback(() => {
    return null;
  }, []);

  const returnToOverview = () => {
    navigate(RoutesEnum.GroupMessageSmsOverview);
  };

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <GridWrapper fixed>
          <Grid item xs={12}>
            <MainContentHeader title={t('groupMessage:sms.create.header')} />
          </Grid>
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <GridWrapper>
                <Grid item xs={12} md={7}>
                  <GridWrapper useBreakpoints={false}>
                    <Grid item xs={12}>
                      <SelectionForm
                        smsMode
                        selectionFromServer={null}
                        fixedSelectionFromServer={null}
                        onSelection={(v) => setSelection(v)}
                        onFixedSelection={(v) => setFixedSelection(v)}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <ControlledTextField
                        name='name'
                        required
                        label={t('groupMessage:sms.formFields.subject')}
                        inputProps={{ maxLength: 50 }} // TODO
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <ControlledTextEditor name='body' smsMode initCallback={textEditorInitalized} onChangeCallback={(val) => setBodyText(val)} />
                      <GroupMessageSmsMessageLength message={bodyText} splitMessageLength={splitMessageLength} />
                    </Grid>
                    <Grid item xs={12}>
                      <ControlledTextField
                        name='recipients'
                        label={t('groupMessage:formFields.recipients')}
                        helperText={t('groupMessage:formFields.recipients_helperTextSMS')}
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                    </Grid>
                  </GridWrapper>
                </Grid>
                <Grid item xs={12} md={5}>
                  <GroupMessageSmsPreview message={bodyText} splitMessageLength={splitMessageLength} disabled={false} />
                </Grid>
                <Grid item xs={12} textAlign='right' sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Stack direction='row' spacing={1}>
                    <Button variant='outlined' type='button' onClick={returnToOverview}>
                      {t('common:button.cancel')}
                    </Button>
                    <LoadingButton
                      loading={isSubmitting}
                      variant='contained'
                      type='submit'
                      disableElevation
                      onClick={() => setSendMode(false)}
                      disabled={Object.keys(errors).length > 0}
                    >
                      {t('common:button.save')}
                    </LoadingButton>
                    <Divider orientation='vertical' variant='middle' flexItem />
                    <LoadingButton
                      loading={isSubmitting}
                      variant='contained'
                      type='submit'
                      disableElevation
                      disabled={Object.keys(errors).length > 0 || (!selection && !fixedSelection)}
                      onClick={() => setSendMode(true)}
                      endIcon={<SendIcon aria-hidden />}
                    >
                      {t('common:button.saveAndSend')}
                    </LoadingButton>
                  </Stack>
                </Grid>
              </GridWrapper>
            </Paper>
          </Grid>
        </GridWrapper>
      </form>
    </FormProvider>
  );
};
